/**
 * CALLS-00 (research/calls-screenpop-design.md §6) — the provider-agnostic
 * call layer. `call_event` rows drive the live dock on every screen; a call
 * that ends un-answered becomes a WORK ITEM in the Pending strip's
 * "Missed calls" lane (locked decision #2); answered/missed calls log onto
 * the customer's one timeline (message channel 'call').
 *
 * Source today = the SIMULATOR (admin-only test ring) so the surface is
 * finished and provable before any phone-vendor decision. The Road-2 adapter
 * (Telnyx front door, pending GM approval) writes the same rows server-side —
 * these helpers are the reference implementation for it.
 *
 * Matching: pegasus_match_phones (last-10, unique-custno-only) — same engine
 * as Podium texts and the on-call sheets. Never silently overwritten.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/** CALLS-AI-01: what the AI heard on the call — SUGGESTIONS only, rendered as
 *  tap-to-accept chips. Nothing here ever writes itself to a record. */
export interface AiExtract {
  caller_name: string | null;
  phones: string[];
  emails: string[];
  address: { add1: string | null; city: string | null; state: string | null; zip: string | null };
  intent: string | null;
  /** CALL-ROUTE-01: every actionable thing said, classified by WHERE it
   *  belongs — job (this visit), property (durable — one tap files it to
   *  Watch For), one_off (this visit only). */
  facts?: { text: string; kind: "job" | "property" | "one_off"; slot?: string | null }[];
}

export interface CallEvent {
  id: string;
  provider: string;
  provider_call_id: string;
  direction: "inbound" | "outbound";
  from_e164: string | null;
  to_e164: string | null;
  state: "ringing" | "answered" | "completed" | "missed" | "voicemail";
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  answered_by: string | null;
  custno: string | null;
  locno: string | null;
  match_type: "confirmed" | "ambiguous" | "unmatched" | null;
  // CALL-REC-1 + CALLS-AI-01 columns:
  recording_path?: string | null;
  cnam?: string | null;
  transcript?: string | null;
  ai_summary?: string | null;
  ai_extract?: AiExtract | null;
  ai_status?: "pending" | "done" | "failed" | null;
  /** CALLS-NOTES-01: what the dispatcher typed during/after the call. */
  dispatcher_notes?: string | null;
  // Enriched client-side for matched callers (the context-before-answer rule):
  customer_fullname?: string | null;
  city?: string | null;
  has_agreement?: boolean;
  last_visit?: string | null; // ISO date of the latest visit on record
}

const p10 = (s: string | null | undefined) =>
  s ? s.replace(/\D/g, "").slice(-10) : "";

export function formatPhone(e164: string | null): string {
  const d = p10(e164);
  if (d.length !== 10) return e164 ?? "unknown";
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Live calls for the dock: ringing + on-the-phone, newest first. */
export function useLiveCalls() {
  return useQuery({
    queryKey: ["live_calls"],
    staleTime: 5 * 1000,
    refetchInterval: 15 * 1000, // realtime invalidates faster; this is the net
    queryFn: async (): Promise<CallEvent[]> => {
      const { data, error } = await supabase
        .from("call_event")
        .select("*")
        .in("state", ["ringing", "answered"])
        .order("started_at", { ascending: false })
        .limit(12);
      if (error) throw error;
      const rows = (data ?? []) as CallEvent[];
      // Context-before-answer enrichment for matched callers (small keyed
      // batches — the dock holds ≤12 calls): name, city, active agreement,
      // last visit. The dispatcher should know who's calling BEFORE answering.
      const custnos = [...new Set(rows.map((r) => r.custno).filter(Boolean))] as string[];
      if (custnos.length) {
        const today = new Date().toISOString().slice(0, 10);
        const [names, locs, agrs, visits] = await Promise.all([
          supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
          supabase.from("mirror_location").select("custno, locno, city").in("custno", custnos),
          supabase
            .from("mirror_serv_agr")
            .select("custno")
            .eq("sa_inactive", 0)
            .gte("expire_date", today)
            .in("custno", custnos),
          supabase
            .from("mirror_dispatch")
            .select("custno, appointment_date")
            .in("custno", custnos)
            .lte("appointment_date", today)
            .order("appointment_date", { ascending: false })
            .limit(60),
        ]);
        const nameBy = new Map(
          ((names.data ?? []) as { custno: string; fullname: string | null }[]).map(
            (n) => [n.custno, n.fullname],
          ),
        );
        const cityBy = new Map<string, string | null>();
        for (const l of (locs.data ?? []) as { custno: string; locno: string | null; city: string | null }[]) {
          // Prefer the matched location's city; fall back to any of theirs.
          const key = l.custno;
          if (!cityBy.has(key)) cityBy.set(key, l.city);
        }
        const agrSet = new Set(((agrs.data ?? []) as { custno: string }[]).map((a) => a.custno));
        const lastBy = new Map<string, string>();
        for (const v of (visits.data ?? []) as { custno: string; appointment_date: string }[]) {
          if (!lastBy.has(v.custno)) lastBy.set(v.custno, v.appointment_date);
        }
        for (const r of rows) {
          if (!r.custno) continue;
          r.customer_fullname = nameBy.get(r.custno) ?? null;
          r.city = cityBy.get(r.custno) ?? null;
          r.has_agreement = agrSet.has(r.custno);
          r.last_visit = lastBy.get(r.custno) ?? null;
        }
      }
      return rows;
    },
  });
}

/** CALLS-AI-01: run the AI over a call — pass a transcript (simulator / manual
 *  paste), or nothing and the function LISTENS to the stored recording. The
 *  result lands on call_event (transcript, ai_summary, ai_extract). */
export function useCallIntel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { call_id: string; transcript?: string }) => {
      const { data, error } = await supabase.functions.invoke("call-intel", {
        body: { call_id: v.call_id, transcript: v.transcript ?? undefined },
      });
      if (error) throw error;
      const out = data as { ok?: boolean; error?: string };
      if (!out?.ok) throw new Error(out?.error || "AI didn't return a result");
      return out;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["live_calls"] });
      qc.invalidateQueries({ queryKey: ["recent_calls"] });
      qc.invalidateQueries({ queryKey: ["call_recording"] });
    },
  });
}

/** CALLS-AI-02: save what was said (simulator's stand-in for audio). Saving a
 *  transcript is ALL it takes — the database trigger fires call-intel on its
 *  own, exactly like a real recording landing. No button, no prompt. */
export function useSetCallTranscript() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { call_id: string; transcript: string }) => {
      const { error } = await supabase
        .from("call_event")
        .update({ transcript: v.transcript, updated_at: new Date().toISOString() })
        .eq("id", v.call_id);
      if (error) throw error;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["live_calls"] });
      qc.invalidateQueries({ queryKey: ["recent_calls"] });
    },
  });
}

/** CALLS-NOTES-01 — the dispatcher's own words on the call record, typed
 *  right on the dock card while they talk. Booking a visit from the call
 *  carries them into the dispatch note (human words first, AI second). */
export function useSetDispatcherNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; notes: string | null }) => {
      const { error } = await supabase
        .from("call_event")
        .update({ dispatcher_notes: v.notes })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["live_calls"] });
      qc.invalidateQueries({ queryKey: ["recent_calls"] });
    },
  });
}

/** Calls from the last N minutes (any state) — powers the "you were just on
 *  the phone" surfaces: the attach-this-number card on the 360 and the
 *  From-the-call strip on customer search. */
export function useRecentCalls(minutes = 45) {
  return useQuery({
    queryKey: ["recent_calls", minutes],
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    queryFn: async (): Promise<CallEvent[]> => {
      const cutoff = new Date(Date.now() - minutes * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("call_event")
        .select("*")
        .eq("direction", "inbound")
        .gte("started_at", cutoff)
        .order("started_at", { ascending: false })
        .limit(8);
      if (error) throw error;
      return (data ?? []) as CallEvent[];
    },
  });
}

/** Phone → unique custno/locno via the shared matcher. */
async function matchPhone(
  e164: string,
): Promise<{ custno: string | null; locno: string | null; match_type: CallEvent["match_type"] }> {
  const digits = p10(e164);
  if (digits.length !== 10) return { custno: null, locno: null, match_type: "unmatched" };
  const { data } = await supabase.rpc("pegasus_match_phones", { p10s: [digits] });
  const row = ((data ?? []) as { p10: string; custno: string; locno: string | null }[])[0];
  return row
    ? { custno: row.custno, locno: row.locno ?? null, match_type: "confirmed" }
    : { custno: null, locno: null, match_type: "unmatched" };
}

/** Seconds a call rang (for "rang 23s" copy). */
function rangSeconds(c: CallEvent): number {
  const end = c.ended_at ? new Date(c.ended_at).getTime() : Date.now();
  return Math.max(0, Math.round((end - new Date(c.started_at).getTime()) / 1000));
}

/** Log the finished call onto the customer's one timeline (channel 'call'). */
async function logCallMessage(c: CallEvent, outcome: "answered" | "missed") {
  if (!c.custno) return; // unmatched calls live in the lane, not on a record
  const dur =
    outcome === "answered" && c.answered_at && c.ended_at
      ? Math.round(
          (new Date(c.ended_at).getTime() - new Date(c.answered_at).getTime()) / 1000,
        )
      : null;
  const body =
    outcome === "answered"
      ? `Incoming call · answered${c.answered_by ? ` by ${c.answered_by.split("@")[0]}` : ""}${
          dur != null ? ` · ${Math.floor(dur / 60)}m ${dur % 60}s` : ""
        }`
      : `Missed call · rang ${rangSeconds(c)}s`;
  await supabase.from("message").insert({
    id: crypto.randomUUID(),
    custno: c.custno,
    locno: c.locno,
    channel: "call",
    direction: "inbound",
    endpoint: c.from_e164,
    body,
    status: "logged",
    provider: "manual",
    logged_by: `calls:${c.provider}`,
    created_at: c.started_at,
  });
}

/** Missed call → a WORK ITEM in the Missed-calls lane (idempotent per call). */
async function createMissedTicket(c: CallEvent) {
  const name = c.customer_fullname
    ? c.customer_fullname.replace(/\s+\d{4,}$/, "")
    : formatPhone(c.from_e164);
  await supabase.from("pegasus_dispatch").insert({
    lane_key: "missed_call",
    custno: c.custno,
    locno: c.locno,
    customer_name: name,
    phone: formatPhone(c.from_e164),
    summary: `Missed call · rang ${rangSeconds(c)}s`,
    notes:
      c.match_type === "confirmed"
        ? "Caller matched by phone — open the customer and call back."
        : "No match in the system — call back to find out who it was.",
    source: "call",
    status: "pending_schedule",
    created_by: `calls:${c.provider}`,
  });
}

/**
 * "That's me — I answered" (CALLS-01b). Telnyx can't see WHICH desk phone
 * picked up, so attribution comes from the human: the dispatcher who answered
 * claims the call. Claiming sets answered_by (their email) — and flips a
 * still-"ringing" row to answered, which doubles as the manual fix when the
 * carrier's mid-call answered callback doesn't arrive. The dock screen-pops
 * the customer for the CLAIMER only; everyone else's card fades to the ghost
 * pill via realtime.
 */
export function useClaimCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (call: CallEvent) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      if (!email) throw new Error("Not signed in");
      const patch: Record<string, unknown> = {
        answered_by: email,
        updated_at: new Date().toISOString(),
      };
      if (call.state === "ringing") patch.state = "answered";
      if (!call.answered_at) patch.answered_at = new Date().toISOString();
      const { error } = await supabase
        .from("call_event")
        .update(patch)
        .eq("id", call.id)
        // Last-claim-wins is wrong for phones: first claim holds.
        .is("answered_by", null);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["live_calls"] }),
  });
}

/**
 * Manually clear a call from the live dock (CALL-REC-1 safety net, Seth
 * 2026-06-25: "after I hung up it still showed the call active with no way to
 * clear it"). The webhook now auto-closes on hangup, but if a carrier hangup
 * callback is ever missed, the dispatcher can always dismiss the stuck card by
 * hand. Just marks it ended — the call_event (and any recording) is untouched.
 */
export function useEndCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (call: CallEvent) => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("call_event")
        .update({ state: "completed", ended_at: call.ended_at ?? nowIso, updated_at: nowIso })
        .eq("id", call.id);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["live_calls"] }),
  });
}

// ---------------------------------------------------------------------------
// Simulator (admin-only in the dock). The Road-2 adapter replaces these with
// server-side webhook handling — same writes, same rules.
// ---------------------------------------------------------------------------

export function useSimRing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (phone: string) => {
      const e164 = `+1${p10(phone)}`;
      const m = await matchPhone(e164);
      const { error } = await supabase.from("call_event").insert({
        provider: "sim",
        provider_call_id: `sim-${Date.now()}`,
        from_e164: e164,
        to_e164: "+19528819000",
        state: "ringing",
        custno: m.custno,
        locno: m.locno,
        match_type: m.match_type,
      });
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["live_calls"] }),
  });
}

export function useSimAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { call: CallEvent; to: "answered" | "completed" | "missed" }) => {
      const { call, to } = v;
      const patch: Record<string, unknown> = {
        state: to,
        updated_at: new Date().toISOString(),
      };
      if (to === "answered") {
        const { data: u } = await supabase.auth.getUser();
        patch.answered_at = new Date().toISOString();
        patch.answered_by = u?.user?.email ?? null;
      }
      if (to === "completed" || to === "missed") patch.ended_at = new Date().toISOString();
      const { error } = await supabase.from("call_event").update(patch).eq("id", call.id);
      if (error) throw error;

      const after: CallEvent = { ...call, ...(patch as Partial<CallEvent>) };
      if (to === "completed") await logCallMessage(after, "answered");
      if (to === "missed") {
        await logCallMessage(after, "missed");
        await createMissedTicket(after);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["live_calls"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}
